import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdressInput from '../Atoms/AdressInput';
import * as api from '../../api';
import { createUrlWithRedirect } from '../Utils/redirectUtils';

const MEDIA_BASE = process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/";

const MyPointsOfSale = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation('common');
    const fr = i18n.language === 'fr';

    const [user, setUser] = useState(null);
    const [posList, setPosList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [editingPOS, setEditingPOS] = useState(null);
    const [formData, setFormData] = useState({
        titre_affiche: '',
        fiche_agence: '',
        adresse: '',
        design_points_of_sales: '',
        photos_projet: '',
        website: '',
        description: '',
        phone: '',
        email: '',
        opening_hours: '',
        instagram: '',
        facebook: '',
        linkedin: '',
        longitude: '',
        latitude: ''
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Auth guard
    useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) {
            navigate(createUrlWithRedirect('/login', location.pathname + location.search));
            return;
        }
        setUser(props.fullUser);
    }, [props.fullUserLoading, props.fullUser]);

    // Fetch POS list
    useEffect(() => {
        if (user?.user?._id) {
            fetchMyPOS();
        }
    }, [user]);

    const fetchMyPOS = async () => {
        try {
            setLoading(true);
            const response = await api.GetMyPOS({ userid: user.user._id });
            setPosList(response.data || []);
        } catch (err) {
            setError(fr ? 'Erreur lors du chargement de vos points de vente' : 'Error loading your points of sale');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const clearFeedback = () => {
        setTimeout(() => { setFeedback(''); setError(''); }, 5000);
    };

    const resetForm = () => {
        setFormData({
            titre_affiche: '', fiche_agence: user?.hsCompany?.properties?.name || '', adresse: '',
            design_points_of_sales: '', photos_projet: '', website: '',
            description: '', phone: '', email: '', opening_hours: '',
            instagram: '', facebook: '', linkedin: '', longitude: '', latitude: ''
        });
        setLogoFile(null);
        setLogoPreview('');
        setEditingPOS(null);
        setShowForm(false);
        setUploadProgress(0);
    };

    const handleEdit = (pos) => {
        setEditingPOS(pos);
        setFormData({
            titre_affiche: pos.titre_affiche || '',
            fiche_agence: pos.fiche_agence || '',
            adresse: pos.adresse || '',
            design_points_of_sales: pos.design_points_of_sales || '',
            photos_projet: pos.photos_projet || '',
            website: pos.website || '',
            description: pos.description || '',
            phone: pos.phone || '',
            email: pos.email || '',
            opening_hours: pos.opening_hours || '',
            instagram: pos.instagram || '',
            facebook: pos.facebook || '',
            linkedin: pos.linkedin || '',
            longitude: pos.longitude ? String(pos.longitude) : '',
            latitude: pos.latitude ? String(pos.latitude) : ''
        });
        setLogoPreview(pos.logo ? MEDIA_BASE + pos.logo : '');
        setLogoFile(null);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (posId) => {
        if (!window.confirm(fr ? 'Êtes-vous sûr de vouloir supprimer ce point de vente ?' : 'Are you sure you want to delete this point of sale?')) return;
        try {
            await api.DeletePOS({ userid: user.user._id, posid: posId });
            setFeedback(fr ? 'Point de vente supprimé avec succès' : 'Point of sale deleted successfully');
            fetchMyPOS();
            clearFeedback();
        } catch (err) {
            setError(fr ? 'Erreur lors de la suppression du point de vente' : 'Error deleting point of sale');
            clearFeedback();
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        setUploadProgress(0);

        try {
            const submission = new FormData(e.target);
            submission.append('userid', user.user._id);
            submission.append('companyid', user.company?._id || '');

            // Append logo file if present
            if (logoFile) {
                submission.append('logo', logoFile);
            }

            // Get address values from the form (AdressInput uses hidden inputs)
            const addressValue = submission.get('adresse');

            if (!formData.titre_affiche || !addressValue) {
                setError(fr ? 'Le titre et l\'adresse sont requis' : 'Title and address are required');
                setSubmitting(false);
                return;
            }

            // Ensure all form data fields are in submission
            Object.entries(formData).forEach(([key, value]) => {
                if (key !== 'adresse' && key !== 'latitude' && key !== 'longitude') {
                    if (!submission.has(key)) {
                        submission.append(key, value);
                    }
                }
            });

            const onUploadProgress = (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            };

            if (editingPOS) {
                submission.append('posid', editingPOS.id);
                await api.UpdatePOS(submission, onUploadProgress);
                setFeedback(fr ? 'Point de vente mis à jour avec succès' : 'Point of sale updated successfully');
            } else {
                await api.CreatePOS(submission, onUploadProgress);
                setFeedback(fr ? 'Point de vente créé avec succès ! Il apparaîtra sur la carte.' : 'Point of sale created successfully! It will appear on the worldmap.');
            }

            resetForm();
            fetchMyPOS();
            clearFeedback();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.error || (fr ? 'Une erreur est survenue' : 'An error occurred'));
            clearFeedback();
        } finally {
            setSubmitting(false);
        }
    };

    if (props.fullUserLoading || !user) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <div>{fr ? 'Chargement...' : 'Loading...'}</div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '10px' }}>
                {fr ? 'Mes points de vente' : 'My Points of Sale'}
            </h1>
            <p style={{ color: '#666', marginBottom: '30px' }}>
                {fr ? 'Gérez vos points de vente affichés sur la carte.' : 'Manage your retail locations displayed on the worldmap.'}
            </p>

            {/* Feedback */}
            {feedback && (
                <div style={{ padding: '12px 20px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '6px', marginBottom: '20px', border: '1px solid #c3e6cb' }}>
                    {feedback}
                </div>
            )}
            {error && (
                <div style={{ padding: '12px 20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '20px', border: '1px solid #f5c6cb' }}>
                    {error}
                </div>
            )}

            {/* Upload progress */}
            {submitting && uploadProgress > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <div style={{ height: '6px', backgroundColor: '#e9ecef', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${uploadProgress}%`, backgroundColor: '#0039d6', transition: 'width 0.3s' }} />
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', textAlign: 'center' }}>{uploadProgress}%</div>
                </div>
            )}

            {/* Add button */}
            {!showForm && (
                <button
                    onClick={() => { resetForm(); setShowForm(true); }}
                    style={{
                        padding: '12px 24px', backgroundColor: '#0039d6', color: 'white',
                        border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '15px',
                        fontWeight: '600', marginBottom: '30px'
                    }}
                >
                    {fr ? '+ Ajouter un point de vente' : '+ Add a Point of Sale'}
                </button>
            )}

            {/* Form */}
            {showForm && (
                <div style={{ backgroundColor: '#f8f9fa', padding: '30px', borderRadius: '10px', marginBottom: '30px', border: '1px solid #e9ecef' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                        <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                            {editingPOS ? (fr ? 'Modifier le point de vente' : 'Edit Point of Sale') : (fr ? 'Nouveau point de vente' : 'New Point of Sale')}
                        </h2>
                        <button onClick={resetForm} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>
                            &times;
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* Logo */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Logo</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <div style={{
                                    width: '100px', height: '100px', borderRadius: '10px',
                                    overflow: 'hidden', backgroundColor: '#e9ecef', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', border: '2px dashed #ccc'
                                }}>
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '5px' }} />
                                    ) : (
                                        <span style={{ color: '#999', fontSize: '12px', textAlign: 'center' }}>{fr ? 'Aucun logo' : 'No logo'}</span>
                                    )}
                                </div>
                                <div>
                                    <input
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        onChange={handleLogoChange}
                                        style={{ fontSize: '14px' }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>PNG, JPG ou WebP.</div>
                                </div>
                            </div>
                        </div>

                        {/* Title */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>{fr ? 'Titre / Nom affiché *' : 'Title / Display Name *'}</label>
                            <input
                                type="text" name="titre_affiche" value={formData.titre_affiche}
                                onChange={handleChange} required style={inputStyle}
                                placeholder={fr ? 'Nom du magasin ou showroom' : 'Store or showroom name'}
                            />
                        </div>

                        {/* Agency */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>{fr ? 'Référence agence' : 'Agency Reference'}</label>
                            <input
                                type="text" name="fiche_agence" value={formData.fiche_agence}
                                onChange={handleChange} style={inputStyle}
                                placeholder={fr ? 'Nom de l\'agence ou de la marque' : 'Agency or brand name'}
                            />
                        </div>

                        {/* Address */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>{fr ? 'Adresse *' : 'Address *'}</label>
                            <AdressInput
                                adressInputName="adresse"
                                latitutdeInputName="latitude"
                                longitudeInputName="longitude"
                                placeholder={fr ? 'Saisissez l\'adresse complète' : 'Enter full address'}
                                required={true}
                                adressDefaultValue={formData.adresse}
                                latitudeDefaultValue={formData.latitude}
                                longitudeDefaultValue={formData.longitude}
                            />
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>Description</label>
                            <textarea
                                name="description" value={formData.description}
                                onChange={handleChange} style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                                placeholder={fr ? 'Décrivez votre point de vente' : 'Describe your point of sale'}
                            />
                        </div>

                        {/* Website */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>{fr ? 'Site web' : 'Website'}</label>
                            <input
                                type="url" name="website" value={formData.website}
                                onChange={handleChange} style={inputStyle}
                                placeholder="https://www.example.com"
                            />
                        </div>

                        {/* Contact info */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                            <div>
                                <label style={labelStyle}>{fr ? 'Téléphone' : 'Phone'}</label>
                                <input
                                    type="tel" name="phone" value={formData.phone}
                                    onChange={handleChange} style={inputStyle}
                                    placeholder="+33 1 23 45 67 89"
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>Email</label>
                                <input
                                    type="email" name="email" value={formData.email}
                                    onChange={handleChange} style={inputStyle}
                                    placeholder="contact@example.com"
                                />
                            </div>
                        </div>

                        {/* Opening hours */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={labelStyle}>{fr ? 'Horaires d\'ouverture' : 'Opening Hours'}</label>
                            <textarea
                                name="opening_hours" value={formData.opening_hours}
                                onChange={handleChange} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                                placeholder={fr ? 'Lun-Ven : 9h-18h\nSam : 10h-17h' : 'Mon-Fri: 9am-6pm\nSat: 10am-5pm'}
                            />
                        </div>

                        {/* Social media */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ ...labelStyle, marginBottom: '10px' }}>{fr ? 'Réseaux sociaux' : 'Social Media'}</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <input
                                    type="url" name="instagram" value={formData.instagram}
                                    onChange={handleChange} style={inputStyle}
                                    placeholder="Instagram URL"
                                />
                                <input
                                    type="url" name="facebook" value={formData.facebook}
                                    onChange={handleChange} style={inputStyle}
                                    placeholder="Facebook URL"
                                />
                                <input
                                    type="url" name="linkedin" value={formData.linkedin}
                                    onChange={handleChange} style={inputStyle}
                                    placeholder="LinkedIn URL"
                                />
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit" disabled={submitting}
                            style={{
                                width: '100%', padding: '14px', backgroundColor: submitting ? '#ccc' : '#0039d6',
                                color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px',
                                fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {submitting ? (fr ? 'Enregistrement...' : 'Saving...') : (editingPOS ? (fr ? 'Mettre à jour' : 'Update Point of Sale') : (fr ? 'Créer le point de vente' : 'Create Point of Sale'))}
                        </button>
                    </form>
                </div>
            )}

            {/* POS List */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>{fr ? 'Chargement de vos points de vente...' : 'Loading your points of sale...'}</div>
            ) : posList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                    <h3 style={{ marginBottom: '10px', color: '#333' }}>{fr ? 'Aucun point de vente' : 'No points of sale yet'}</h3>
                    <p style={{ color: '#666' }}>{fr ? 'Ajoutez votre premier point de vente pour l\'afficher sur la carte.' : 'Add your first retail location to display it on the worldmap.'}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {posList.map(pos => (
                        <div key={pos.id} style={{
                            display: 'grid', gridTemplateColumns: '90px 1fr auto',
                            gap: '20px', alignItems: 'center', padding: '20px',
                            backgroundColor: 'white', borderRadius: '10px',
                            border: '1px solid #e9ecef', transition: 'box-shadow 0.2s'
                        }}>
                            {/* Logo */}
                            <div style={{
                                width: '90px', height: '90px', borderRadius: '10px',
                                overflow: 'hidden', backgroundColor: '#f0f0f0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #e0e0e0'
                            }}>
                                {pos.logo ? (
                                    <img src={MEDIA_BASE + pos.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '5px' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : pos.photos_projet ? (
                                    <img src={pos.photos_projet.split(',')[0].trim()} alt="Photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <span style={{ color: '#999', fontSize: '14px' }}>{fr ? 'Aucun logo' : 'No logo'}</span>
                                )}
                            </div>

                            {/* Info */}
                            <div>
                                <div style={{ fontSize: '17px', fontWeight: '600', marginBottom: '4px' }}>
                                    {pos.titre_affiche || (fr ? 'Sans titre' : 'Untitled')}
                                </div>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                                    {pos.adresse}
                                </div>
                                {pos.website && (
                                    <a href={pos.website} target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'block', fontSize: '12px', color: '#0039d6', marginTop: '4px' }}>
                                        {pos.website}
                                    </a>
                                )}
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => handleEdit(pos)}
                                    style={{
                                        width: '40px', height: '40px', borderRadius: '8px',
                                        border: '1px solid #0039d6', backgroundColor: 'white',
                                        color: '#0039d6', cursor: 'pointer', fontSize: '16px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                    title={fr ? 'Modifier' : 'Edit'}
                                >
                                    ✎
                                </button>
                                <button
                                    onClick={() => handleDelete(pos.id)}
                                    style={{
                                        width: '40px', height: '40px', borderRadius: '8px',
                                        border: '1px solid #dc3545', backgroundColor: 'white',
                                        color: '#dc3545', cursor: 'pointer', fontSize: '18px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}
                                    title={fr ? 'Supprimer' : 'Delete'}
                                >
                                    &times;
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    fontSize: '14px',
    color: '#333'
};

const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d0d0d0',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box'
};

export default MyPointsOfSale;
